/**
 * useBranding — single-fetch shared hook for the operator's public
 * brand snapshot.
 *
 * Phase 2 P2 #5b (2026-06). Header is mounted on every top-level route
 * (Landing / Login / Register / Passenger / Driver / Admin), so a
 * naive useEffect-per-mount would fire the GET six times on a cold
 * session. We dedupe at the module level: the first caller kicks off
 * the fetch and every subsequent caller gets the same in-flight
 * promise. Result is cached for the lifetime of the JS process, with
 * a deliberate manual-invalidate path for the operator who just saved
 * a brand change in the OnboardingWizard.
 *
 * Failure mode: every consumer falls back to platform defaults
 * (`Crystal Dispatch` brand name, fallback support email). Network
 * outage / 5xx / bad JSON all collapse to the same safe shape.
 */
import { useEffect, useState } from "react";

const DEFAULT_BRAND = {
  company_name: "Crystal Dispatch",
  brand_tagline: "",
  support_email: "support@dispatch.crystalworkforce.com",
  hello_email: "",
  brand_logo_url: "",
  website: "",
};

let _cached = null;        // resolved snapshot
let _inflight = null;      // pending fetch promise

async function fetchBranding() {
  if (_cached) return _cached;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/branding`;
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      _cached = { ...DEFAULT_BRAND, ...data };
      return _cached;
    } catch (e) {
      // Hard fallback. Cache the default snapshot too so we don't retry
      // on every Header re-mount during a known-bad network state.
      _cached = DEFAULT_BRAND;
      return _cached;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function invalidateBrandingCache() {
  _cached = null;
  _inflight = null;
}

export function useBranding() {
  const [brand, setBrand] = useState(_cached || DEFAULT_BRAND);
  useEffect(() => {
    let cancelled = false;
    fetchBranding().then((b) => {
      if (!cancelled) setBrand(b);
    });
    return () => { cancelled = true; };
  }, []);
  return brand;
}

export { DEFAULT_BRAND };
